<template>
    <not-found-view v-if="error" :error500="true"></not-found-view>

    <div v-if="!$apollo.loading && !error" class="game-menu">
        <div class="grid grid--padding">
            <div class="game-menu__container">
                <router-link v-if="editing == 'true' && !fromBudgetRoute" :to="{name: 'game-overview'}" class="btn btn--back">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.0001 7.99991L11.0001 15.9999L19.0001 23.9999" stroke="#F88C50" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </router-link>
                <button v-else @click="goBackToPreviousPhase" class="btn btn--back">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.0001 7.99991L11.0001 15.9999L19.0001 23.9999" stroke="#F88C50" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <template v-for="(group, index) in materialsGrouped" :key="index">
                    <h4 v-if="group.data.length" class="game-menu__title">{{ group.group }}</h4>

                    <menu-card v-for="(material, i) in group.data" 
                        :key="i" 
                        :item="material.node" 
                        :saved="checkSavedMaterial(material.node.id)"
                        :selected="multichoiceMaterials.includes(material.node.id)"
                        :mulitchoice="phaseMultichoice"
                        @select-material="showModal"
                        @add-material="toggleMultichoice"
                    >
                    </menu-card>
                </template>

                <div v-if="phaseMultichoice == 'true' && multichoiceMaterials.length" class="btn-wrapper">
                    <button @click="saveCurrentPhaseMaterial" class="btn btn--primary">{{ $t('button.continue') }}</button>
                </div>
                <div v-if="phaseMultichoice == 'true'" class="btn-wrapper">
                    <button @click="saveCurrentPhaseMaterial" :class="['btn btn--primary', {'disabled': !multichoiceMaterials.length}]">
                        {{ $t('button.continue') }}
                    </button>
                </div>
            </div>

            <div class="game-menu__container">
                <img v-if="!isModalVisible" src="@/assets/images/menu-background.png" :alt="$t('game.menu.background-image.alt')" class="game-menu__img" />

                <menu-modal v-if="isModalVisible" :modalAnimation="menuModalAnimation">
                    <template #content>
                        <div :class="['animate__animated', menuModalAnimation]">
                            <div v-if="modalData.thumbnail" class="menu-modal__img">
                                <img :src="`${apiUri}${modalData.thumbnail}`" :alt="modalData.name" />
                            </div>

                            <div class="menu-modal__title">{{ modalData.name }}</div>
                            <div class="menu-modal__desc animate__animated" v-html="modalData.description"></div>

                            <ul class="price-list">
                                <li class="price-list__item">
                                    <span>{{ $t('game.text.budget') }}</span>
                                    <span v-html="checkSavedMaterial(modalData.id) ? `${budgetAmountFormatted()} ${budgetCurrency}` : `${budgetAmountFormatted()} ${budgetCurrency}`"></span>
                                </li>
                                <li class="price-list__item price-list__item--highlighted">
                                    <span>{{ $t('game.text.item-price') }}</span>
                                    <span>{{ calculatedPriceFormatted }} {{ budgetCurrency }}</span>
                                </li>
                                <li class="price-list__item">
                                    <span>{{ $t('game.text.remaining-budget') }}</span>
                                    <span v-html="checkSavedMaterial(modalData.id) ? `${remainingBudget()} ${budgetCurrency}` : `${remainingBudget()} ${budgetCurrency}`" class="price-list__price"></span>
                                </li>
                            </ul>
                        </div>
                    </template>

                    <template #actions>
                        <template v-if="phaseMultichoice == 'true'">
                            <button v-if="!multichoiceMaterials.includes(modalData.id)" @click="toggleMultichoice(modalData.id, 'add')" class="btn btn--primary">{{ $t('button.select') }}</button>
                            <button @click="closeModal" class="btn">{{ $t('button.back') }}</button>
                        </template>
                        <template v-else>
                            <button @click="saveCurrentPhaseMaterial" class="btn btn--primary">{{ $t('button.select') }}</button>
                            <button @click="closeModal" class="btn">{{ $t('button.cancel') }}</button>
                        </template>
                    </template>
                </menu-modal>
            </div>
        </div>
    </div>
</template>

<script>
import { getHouseMaterialListing, getHouseBuildingPhaseListing } from '@/queries/houseBuildingPhasesQueries.js';
import MenuCard from '@/components/game/MenuCard.vue';
import MenuModal from '@/components/game/MenuModal.vue';

export default {
    name: 'GameMenuView',
    components: {
        MenuCard,
        MenuModal
    },
    props: ['phaseId', 'phaseKey', 'phaseMultichoice', 'editing'],
    emits: ['open-model'],
    inject: ['calculateFullPrice'],
    data() {
        return {
            error: false,
            getHouseMaterialListing: null,
            getHouseBuildingPhaseListing: null,
            modalData: null,
            isModalVisible: false,
            selectedMaterialId: null,
            multichoiceMaterials: [],
            fromBudgetRoute: false,
            menuModalAnimation: null
        }
    },
    apollo: {
        getHouseMaterialListing: {
            query: getHouseMaterialListing,
            variables() {
                return {
                    filter: '{"phase": {"$like" :"%' + this.phaseId + '"}}'
                }
            },
            error(error) {
                console.log('errors', error);
                this.error = true;
            }
        },
        getHouseBuildingPhaseListing: {
            query: getHouseBuildingPhaseListing,
            error(error) {
                console.log('errors', error);
                this.error = true;
            }
        }
    },
    computed: {
        materials() {
            return this.getHouseMaterialListing;
        },
        materialsGrouped() {
            let materialsGrouped = [];
            
            if (this.materials) {
                this.materials.edges.forEach(material => {
                    if (material.node.materialGroup) {
                        const group = materialsGrouped.filter(mat => mat.group == material.node.materialGroup.name)
                        
                        if (!group.length) {
                            materialsGrouped.push({group: material.node.materialGroup.name, data: []});
                        }
                    }
                });
                
                materialsGrouped.push({group: null, data: []});

                this.materials.edges.forEach(material => {
                    if (material.node.materialGroup) {
                        const groupIndex = materialsGrouped.findIndex(group => group.group == material.node.materialGroup.name);
                        
                        if (groupIndex >= 0) {
                            materialsGrouped[groupIndex].data.push(material);
                        }
                    } else {
                        const noGroupIndex = materialsGrouped.findIndex(group => group.group == null);
                        materialsGrouped[noGroupIndex].data.push(material);
                    }
                });
            }
            
            return materialsGrouped;
        },
        savedMaterials() {
            const saved = this.$store.getters['game/getSavedMaterials'];
            const phase = saved.filter(material => material.id == this.phaseId);

            if (phase.length && this.phaseMultichoice == 'true') {
                if (phase[0].data) {
                    phase[0].data.forEach(material => {
                        this.multichoiceMaterials.push(material.uniqueId);
                    });
                }
            }
            
            return phase[0];
        },
        surfaceArea() {
            return this.$store.getters['game/getSurfaceArea'];
        },
        budgetAmount() {
            return this.$store.getters['game/getBudgetAmount'];
        },
        budgetCurrency() {
            return this.$store.getters['game/getBudgetCurrency'];
        },
        calculatedPrice() {
            return this.calculateFullPrice(this.modalData.name, this.modalData.price.value, this.modalData.price.unit, this.phaseKey);
        },
        calculatedPriceFormatted() {
            return Number(this.calculatedPrice).toLocaleString('hr');
        },
        vectaryModel() {
            return this.$store.getters['game/getVectaryModel'];
        },
        apiUri() {
            return process.env.VUE_APP_APOLLO_API_URI;
        },
        currentPhase() {
            return this.$store.getters['game/getCurrentPhase'];
        }
    },
    methods: {
        budgetAmountFormatted() {
            if (this.savedMaterials) {
                let price = 0;

                if (this.savedMaterials.data) {
                    this.savedMaterials.data.forEach(material => {
                        price = price + this.calculateFullPrice(material.name, material.price.value, material.price.unit, this.savedMaterials.key);
                    });
                }

                return Number(this.budgetAmount + price).toLocaleString('hr');
            } else {
                return Number(this.budgetAmount).toLocaleString('hr');
            }
        },
        remainingBudget() {
            if (this.savedMaterials) {
                let budget;
                let price = 0;

                if (this.savedMaterials.data) {
                    this.savedMaterials.data.forEach(material => {
                        price = price + this.calculateFullPrice(material.name, material.price.value, material.price.unit, this.savedMaterials.key);
                    });
                }

                budget = this.budgetAmount + price;
                return Number(budget - this.calculatedPrice).toLocaleString('hr');
            } else {
                return Number(this.budgetAmount - this.calculatedPrice).toLocaleString('hr');
            }
        },
        showModal(materialId) {
            const selected = this.materials.edges.filter(material => material.node.id == materialId);

            this.modalData = selected[0].node;
            this.isModalVisible = true;
            this.selectedMaterialId = materialId;
        },
        closeModal() {
            this.isModalVisible = false;
        },
        showVectaryObject(materialId, materialVectary) {
            const allSavedMaterials = this.$store.getters['game/getSavedMaterials'];
            let hasSpecialConditions = false;
            
            materialVectary.forEach(object => {
                if (object.relatedModel) {
                    allSavedMaterials.forEach(phase => {
                        if (phase.data) {
                            phase.data.forEach(material => {
                                if (material.uniqueId == object.relatedModel.id) {
                                    hasSpecialConditions = true;

                                    if (object.vectaryId && this.vectaryModel) {
                                        console.log("set visibility true for:", object.vectaryId);
                                        this.vectaryModel.setVisibility(object.vectaryId, true, false);
                                    }
                                }                            
                            });
                        }
                    });
                }
            });

            if (!hasSpecialConditions) {
                const object = materialVectary.filter(object => object.relatedModel == null);

                if (object[0].vectaryId && this.vectaryModel) {
                    console.log("set visibility true for:", object[0].vectaryId);
                    this.vectaryModel.setVisibility(object[0].vectaryId, true, false);
                }

                allSavedMaterials.forEach(phase => {
                    if (phase.data) {
                        phase.data.forEach(mat => {
                            mat.buidlingVectaryModel.forEach(object => {
                                if (object.relatedModel && object.relatedModel.id == materialId) {
                                    console.log("set visibility true for:", object.vectaryId);
                                    this.vectaryModel.setVisibility(`${object.vectaryId}`, true, false);

                                    const defaultObject = mat.buidlingVectaryModel.filter(object => object.relatedModel == null);

                                    if (defaultObject[0].vectaryId && this.vectaryModel) {
                                        console.log("set visibility false for:", defaultObject[0].vectaryId);
                                        this.vectaryModel.setVisibility(defaultObject[0].vectaryId, false, false);
                                    }
                                }
                            });
                        });
                    }
                });
            }
        },
        saveCurrentPhaseMaterial() {
            this.hidePreviousVectaryObject();

            let budgetRoute = this.fromBudgetRoute;
            let id = this.phaseId;
            let data = [];

            if (this.multichoiceMaterials.length) {
                this.multichoiceMaterials.forEach(choice => {
                    const selected = this.materials.edges.filter(material => material.node.id == choice);
                    const value = {
                        uniqueId: selected[0].node.id,
                        name: selected[0].node.name,
                        description: selected[0].node.description,
                        icon: selected[0].node.icon,
                        thumbnail: selected[0].node.thumbnail,
                        price: selected[0].node.price,
                        vectaryElementId: selected[0].node.vectaryElementId,
                        buidlingVectaryModel: selected[0].node.buidlingVectaryModel
                    }

                    this.showVectaryObject(value.uniqueId, value.buidlingVectaryModel);
                    data.push(value);
                });
            } else {
                const selected = this.materials.edges.filter(material => material.node.id == this.selectedMaterialId);
                const value = {
                    uniqueId: selected[0].node.id,
                    name: selected[0].node.name,
                    description: selected[0].node.description,
                    icon: selected[0].node.icon,
                    thumbnail: selected[0].node.thumbnail,
                    price: selected[0].node.price,
                    vectaryElementId: selected[0].node.vectaryElementId,
                    buidlingVectaryModel: selected[0].node.buidlingVectaryModel
                }

                this.showVectaryObject(value.uniqueId, value.buidlingVectaryModel);
                data.push(value);
            }

            this.$store.dispatch('game/saveSelectedMaterial', {id, data, budgetRoute});
        },
        checkSavedMaterial(id) {
            if (this.savedMaterials) {
                const savedMaterial = this.savedMaterials.data.filter(material => material.uniqueId == id);

                if (savedMaterial.length) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        toggleMultichoice(id, action = 'toggle') {
            if (this.multichoiceMaterials.includes(id) && action != 'add') {
                this.multichoiceMaterials.splice(this.multichoiceMaterials.indexOf(id), 1);
            } else {
                this.multichoiceMaterials.push(id);
            }
        },
        hidePreviousVectaryObject() {
            if (this.savedMaterials && this.vectaryModel) {
                if (this.savedMaterials.data) {
                    this.savedMaterials.data.forEach(material => {
                        material.buidlingVectaryModel.forEach(object => {
                            console.log("set visibility false for:", object.vectaryId);
                            this.vectaryModel.setVisibility(`${object.vectaryId}`, false, false);

                            const allSavedMaterials = this.$store.getters['game/getSavedMaterials'];

                            allSavedMaterials.forEach(phase => {
                                if (phase.data) {
                                phase.data.forEach(mat => {
                                    mat.buidlingVectaryModel.forEach(object => {
                                        if (object.relatedModel && object.relatedModel.id == material.uniqueId) {
                                            console.log("set visibility false for:", object.vectaryId);
                                            this.vectaryModel.setVisibility(`${object.vectaryId}`, false, false);

                                            const defaultObject = mat.buidlingVectaryModel.filter(object => object.relatedModel == null);

                                            if (defaultObject[0].vectaryId && this.vectaryModel) {
                                                console.log("set visibility true for:", defaultObject[0].vectaryId);
                                                this.vectaryModel.setVisibility(defaultObject[0].vectaryId, true, false);
                                            }
                                        }
                                    });
                                });
                                }
                            });
                        });
                    });
                }
            }
        },
        goBackToPreviousPhase() {
            if (this.fromBudgetRoute) {
                this.$router.push({name: 'budget'});
            } else if(this.currentPhase != 0 && this.savedMaterials == undefined || this.currentPhase != 0 && !this.savedMaterials.data.length) {
                const allSavedMaterials = this.$store.getters['game/getSavedMaterials']
                const phaseDisabledBy = allSavedMaterials[this.currentPhase - 1].disabledByMaterial;
                let skipPhase;

                allSavedMaterials.forEach(phase => {
                    if (phase.data.length) {
                        phase.data.forEach(material => {
                            if (phaseDisabledBy) {
                                phaseDisabledBy.forEach(mat => {
                                    if (material.uniqueId == mat.id) {
                                        skipPhase = true;
                                    }
                                });
                            }
                        });
                    }
                });

                let prevPhase = skipPhase ? this.currentPhase - 2 : this.currentPhase - 1;
                this.$store.commit('game/updatePhase', prevPhase);
                this.$router.push({name: 'game'});
            } else {
                this.$router.push({name: 'game'});
            }
        }
    },
    watch: {
        modalData(_newValue, oldValue) {

            if (oldValue != null) {
                this.menuModalAnimation = null;
                setTimeout(() => {
                    this.menuModalAnimation = 'animate__flipInY';
                }, 50);
            }
        }
    },
    beforeRouteEnter(_to, from, next) {
        if (from.name == 'budget') {
            next(vm => {
                vm.fromBudgetRoute = true;
            });
        } else {
            next();
        }
    }
}
</script>
